import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { Scenario_Statuses_Enum } from "generated/graphql";
import {
    segmentTrackCreateBulkScenarios,
    segmentTrackSubmitScenarioRun
} from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useSpeculativeProjectsInCluster } from "in_queue/contexts/ProjectDataContext";
import { TrackedProject } from "in_queue/types/projectType";
import { ScenarioStage } from "in_queue/types/scenarioType";
import { useState } from "react";
import { useCreateScenarios } from "../ScenarioBuilder/scenarioBuilder.helpers";
import { BulkScenarioDraft } from "./bulkScenarioDrafts";

/** Create one scenario per draft in a single transaction; resolves to the created count, 0 on failure. */
export const useCreateBulkScenarios = ({
    project,
    scenarioStage
}: {
    project: TrackedProject;
    scenarioStage: ScenarioStage;
}): {
    createBulkScenarios: (
        drafts: ReadonlyArray<BulkScenarioDraft>
    ) => Promise<number>;
    isCreating: boolean;
} => {
    const createScenarios = useCreateScenarios();
    const cluster = useCluster();
    const userData = useUserDataContext();
    const speculativeProjects = useSpeculativeProjectsInCluster();
    const [isCreating, setIsCreating] = useState(false);

    const createBulkScenarios = async (
        drafts: ReadonlyArray<BulkScenarioDraft>
    ): Promise<number> => {
        if (speculativeProjects === "loading" || drafts.length === 0) {
            return 0;
        }

        setIsCreating(true);
        const result = await createScenarios(
            drafts.map((draft) => ({
                cluster,
                project,
                userData,
                title: draft.title,
                runOnSubmit: draft.runOnSubmit,
                projectSizeAssumptions: draft.projectSizeAssumptions,
                scenarioStage,
                costAssumptions: draft.costAssumptions,
                includedSpeculativeProjects: speculativeProjects.filter((sp) =>
                    draft.includedSpeculativeProjectIds.includes(sp.projectId)
                ),
                customUpgradeId: draft.customUpgradeId
            }))
        ).catch(() => ({ created: [], archivedCount: 0 }));
        setIsCreating(false);

        const runRequested = result.created.filter(
            (scenario) =>
                scenario.status === Scenario_Statuses_Enum.RunRequested
        );
        runRequested.forEach((scenario) =>
            segmentTrackSubmitScenarioRun({
                scenarioId: scenario.scenario_id,
                source: "bulk_upload"
            })
        );
        if (result.created.length > 0) {
            segmentTrackCreateBulkScenarios({
                cluster,
                projectId: project.projectId,
                scenarioStage,
                numScenarios: result.created.length,
                numRunOnSubmit: runRequested.length
            });
        }

        return result.created.length;
    };

    return { createBulkScenarios, isCreating };
};
